import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef } from "@angular/core";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { ReactiveFormsModule, FormControl, FormGroup } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { MatChipsModule } from "@angular/material/chips";
import { getDictionaryItem } from "../../utils/dictionary.domain";
import { BusinessAreaService } from "../../services/business-area.service";
import { BusinessArea } from "../../interfaces/business-area-ce.interface";
import { FilterableItem, FilterType } from "../../interfaces/filter.interface";
import { AccountsService } from "../../services/accounts.service";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { provideNativeDateAdapter } from "@angular/material/core";
import { MAT_DATE_FORMATS, DateAdapter } from "@angular/material/core";
import { CommonModule } from "@angular/common";

export const DATE_FORMATTING = {
    parse: {
        dateInput: "DD/MM/YYYY"
    },
    display: {
        dateInput: "DD/MM/YYYY",
        monthYearLabel: "MMM YYYY",
        dateA11yLabel: "DD/MM/YYYY",
        monthYearA11yLabel: "MMMM YYYY"
    }
};

@Component({
    selector: "app-filter",
    imports: [
        CommonModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatIconModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatChipsModule,
        MatButtonModule,
        MatDividerModule,
        MatDatepickerModule
    ],
    templateUrl: "./filter.component.html",
    styleUrl: "./filter.component.scss",
    providers: [provideNativeDateAdapter(), { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATTING }]
})
export class FilterComponent implements OnInit {
    @Input() itemList: FilterableItem[] | undefined = [];
    @Input() reportedByIds: string[] | null = null;
    @Input() caseStatuses: { code: number; name: string }[] = [];
    @Input() type: FilterType = FilterType.Default;
    @Output() filterChanged = new EventEmitter<{
        search: string;
        businessAreas: BusinessArea[];
        contactId?: string[];
        statuscode?: number[];
        startDate: any;
        endDate: any;
    }>();
    @Output() sortByDayOfCourse = new EventEmitter<boolean>();
    public searchTestId = `filter-search-input-${this.filterText}`;
    public tempInputValue: string = "";
    public selectedContactIds: string[] = [];
    public selectedStatusCodes: number[] = [];
    public showExtendedFilters: boolean = false;

    public businessAreasControl = new FormControl<BusinessArea[]>([]);
    public filteredItemList: FilterableItem[] = [];
    public isSortByDateAscending: boolean = true;
    public businessAreas: BusinessArea[] = [];
    public contactCases: { contactId: string; name: string }[] = [];

    constructor(
        private businessAreaService: BusinessAreaService,
        private accountsService: AccountsService,
        private dateAdapter: DateAdapter<Date>,
        private cdr: ChangeDetectorRef
    ) {
        this.dateAdapter.setLocale("da-DK");
    }

    dateRange = new FormGroup({
        start: new FormControl<Date | null>(null),
        end: new FormControl<Date | null>(null)
    });

    get FilterType() {
        return FilterType;
    }

    get filterText() {
        switch (this.type) {
            case FilterType.Case:
                return "Supportsager";
            case FilterType.Knowledge:
                return "Vidensartikler";
            case FilterType.Message:
                return "Beskeder";
            case FilterType.Course:
                return "Kurser";
            default:
                return "";
        }
    }

    ngOnInit() {
        this.businessAreaService.getAreas().subscribe((areas: BusinessArea[]) => {
            if (!areas) return;
            this.businessAreas = areas;

            // we set a timeout here do to a weird bug where the business areas return [] on routerLink navigation
            setTimeout(() => {
                this.filterBusinessAreas(this.businessAreas);
            }, 100);
        });

        if (this.reportedByIds !== null) {
            this.accountsService.getConnectedContactPersons(localStorage.getItem("accountid")).subscribe((contacts) => {
                if (!contacts) return;

                this.contactCases = contacts
                    .filter((item) => this.reportedByIds?.includes(item.contactId ?? ""))
                    .map((item) => ({
                        contactId: item.contactId,
                        name: item.fullname ?? ""
                    }));
            });
        }
    }

    filterBusinessAreas(areas: BusinessArea[]) {
        if (!this.itemList || this.businessAreas.length === 0) return;

        this.businessAreas = areas.filter((area) => {
            return this.itemList?.some((item) => item.businessAreaId?.toLowerCase() === area.id.toLowerCase());
        });

        this.countBusinessAreas();
    }

    onContactSelectionChange(selectedIds: string[]) {
        this.selectedContactIds = selectedIds;
        this.emitFilterChanged();
    }

    onStatuscodeSelectionChange(selectedCodes: number[]) {
        this.selectedStatusCodes = selectedCodes;
        this.emitFilterChanged();
    }

    filterArea() {
        this.filterItems(this.tempInputValue);
    }

    filterItems(inputValue: string) {
        this.tempInputValue = inputValue;
        if (!this.itemList) return;

        let tempFilteredItemList = this.itemList;

        if (this.businessAreasControl.value && this.businessAreasControl.value.length > 0) {
            const areas = this.businessAreasControl.value;
            tempFilteredItemList = tempFilteredItemList.filter((item) => {
                const keyword = item.businessAreaId ?? "";
                return areas.some((area) => keyword.toLowerCase() === area.id.toLowerCase());
            });
        }

        if (inputValue) {
            if (inputValue.length > 1) {
                tempFilteredItemList = tempFilteredItemList.filter((item) => {
                    return item.name?.toLowerCase().includes(inputValue.toLowerCase()) || item.description?.toLowerCase().includes(inputValue.toLowerCase());
                });
            }
        }

        this.filteredItemList = tempFilteredItemList;
        this.emitFilterChanged();
    }

    onBusinessAreasRemoved(area: BusinessArea) {
        const areas = this.businessAreasControl.value ?? [];
        this.removeFirst(areas, area);
        this.businessAreasControl.setValue([...areas]);
        this.emitFilterChanged();
    }

    toggleFilterArea(area: BusinessArea) {
        const areas = this.businessAreasControl.value ?? [];
        if (areas.some((a) => a.id === area.id)) {
            this.removeFirst(areas, area);
        } else {
            areas.push(area);
        }
        this.businessAreasControl.setValue([...areas]);
        this.emitFilterChanged();
    }

    isSelectedArea(area: BusinessArea) {
        return this.businessAreasControl.value?.some((a) => a.id === area.id);
    }

    private removeFirst<T>(array: T[], toRemove: T): void {
        const index = array.indexOf(toRemove);
        if (index !== -1) {
            array.splice(index, 1);
        }
    }

    private countBusinessAreas() {
        if (!this.itemList || !this.businessAreas) return;

        this.businessAreas.forEach((area) => (area.count = 0));

        this.businessAreas.forEach((area) => {
            area.count =
                this.itemList?.filter((item) => {
                    const keyword = item.businessAreaId ?? "";
                    return keyword.toLowerCase() === area.id.toLowerCase();
                }).length ?? 0;
        });

        this.businessAreas = [...this.businessAreas];
        this.cdr.detectChanges();
    }

    public clearDateRange() {
        this.dateRange.setValue({ start: null, end: null });
        this.emitFilterChanged();
    }

    public emitFilterChanged() {
        this.filterChanged.emit({
            search: this.tempInputValue,
            businessAreas: this.businessAreasControl.value ?? [],
            contactId: this.selectedContactIds,
            statuscode: this.selectedStatusCodes,
            startDate: this.dateRange.value.start,
            endDate: this.dateRange.value.end
        });
    }

    public toggleFilters() {
        this.showExtendedFilters = !this.showExtendedFilters;
    }

    public removeContactFilter(contactId: string) {
        this.selectedContactIds = this.selectedContactIds.filter((id) => id !== contactId);
        this.emitFilterChanged();
    }

    public removeStatusFilter(statusCode: number) {
        this.selectedStatusCodes = this.selectedStatusCodes.filter((code) => code !== statusCode);
        this.emitFilterChanged();
    }

    public removeAllFilters() {
        this.selectedContactIds = [];
        this.selectedStatusCodes = [];
        this.clearDateRange();
        this.emitFilterChanged();
    }

    getContactName(contactId: string): string {
        const contact = this.contactCases.find((c) => c.contactId === contactId);
        return contact ? contact.name : contactId;
    }

    getStatusName(statusCode: number): string {
        const status = this.caseStatuses.find((s) => s.code === statusCode);
        return status ? status.name : statusCode.toString();
    }

    getDictionaryItem = getDictionaryItem;
}
